'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

/**
 * "Data translation" pillar section. Visual metaphor: messy/rotated cards on
 * the left (unstructured input), a transformer pill in the middle, and a
 * clean monospace schema preview on the right that auto-cycles through
 * representative output formats (FHIR, JSON, SQL, XML).
 */
export function DataTranslationVisual() {
  const t = useTranslations('dataTranslationVisual');

  const inputs = t.raw('inputs') as string[];
  const outputs = t.raw('outputs') as string[];
  const examples = t.raw('examples') as { label: string; sublabel: string }[];

  const inputCards = inputs.map((label, i) => ({
    label,
    icon: INPUT_ICONS[i % INPUT_ICONS.length],
    rotate: ROTATIONS[i % ROTATIONS.length],
  }));

  // Pair each translated label with a fixed code snippet (snippets are not
  // translated — they're literal payload examples). Ordering must match the
  // examples array in messages/*.json.
  const exampleSnippets = examples.map((e, i) => ({
    label: e.label,
    sublabel: e.sublabel,
    snippet: SNIPPETS[i % SNIPPETS.length],
  }));

  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Gate the rotation on visibility — no point ticking state every 3.5s
  // (and triggering a re-render + animate-fade-in keyframes) for a section
  // the user can't see yet.
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(entries => {
      for (const e of entries) setInView(e.isIntersecting);
    }, { rootMargin: '128px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !inView) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % exampleSnippets.length);
    }, 3500);
    return () => clearInterval(id);
  }, [paused, inView, exampleSnippets.length]);

  return (
    <section ref={sectionRef} className="cv-auto bg-background-primary py-24 px-[10%]">
      <div className="max-w-6xl flex flex-col gap-14">

        {/* Heading */}
        <div className="flex flex-col gap-5 max-w-6xl">
          <p className="font-mono text-eyebrow tracking-[var(--hero-label-tracking)] uppercase text-font-navy">
            {t('eyebrow')}
          </p>
          <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-secondary">
            {t('title')}
          </h2>
          <p className="text-subheadline text-font-tertiary/80 font-light leading-relaxed">
            {t('subtitle')}
          </p>
        </div>

        {/* Visual: input → transformer → output */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-10 lg:gap-6 items-center">

          {/* LEFT — messy unstructured inputs */}
          <div className="flex flex-col gap-4">
            <p className="font-mono text-eyebrow uppercase tracking-widest text-font-tertiary/60 lg:text-end">
              {t('inputs_label')}
            </p>
            {/* Mobile/tablet: 2-column grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 py-2 overflow-visible lg:hidden">
              {inputCards.map(({ label, icon, rotate }, i) => (
                <div
                  key={i}
                  className="relative flex items-center gap-2 rounded-xl border border-black/[0.08] bg-white px-3 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-transform duration-300"
                  style={{ transform: `rotate(${rotate}deg)` }}
                  aria-label={label}
                >
                  <div className="shrink-0 size-7 rounded-lg bg-stats-bg flex items-center justify-center text-font-secondary/70">
                    {icon}
                  </div>
                  <span className="text-[length:var(--text-eyebrow)] text-font-secondary leading-tight">{label}</span>
                </div>
              ))}
            </div>
            {/* Desktop: centered vertical stack with per-card rotation */}
            <div className="hidden lg:flex lg:flex-col items-end gap-2.5 py-2 overflow-visible lg:max-w-sm">
              {inputCards.map(({ label, icon, rotate }, i) => (
                <div
                  key={i}
                  className="relative flex items-center gap-2 rounded-xl border border-black/[0.08] bg-white px-3 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-transform duration-300 w-fit max-w-[240px]"
                  style={{ transform: `rotate(${rotate}deg)` }}
                  aria-label={label}
                >
                  <div className="shrink-0 size-7 rounded-lg bg-stats-bg flex items-center justify-center text-font-secondary/70">
                    {icon}
                  </div>
                  <span className="text-[length:var(--text-eyebrow)] text-font-secondary leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* MIDDLE — transformer pill + directional connectors */}
          <div className="flex flex-col lg:flex-row items-center gap-3 lg:gap-4 py-2 lg:py-0 lg:px-1">
            <ConnectorArrow />
            <span className="font-mono text-eyebrow uppercase tracking-wide leading-snug text-center px-4 py-2 rounded-2xl border border-dashed border-font-secondary/25 text-font-secondary/50 bg-background-primary max-w-[240px]">
              {t('arrow_label')}
            </span>
            <ConnectorArrow />
          </div>

          {/* RIGHT — clean structured output */}
          <div
            className="flex flex-col gap-4"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <p className="font-mono text-eyebrow uppercase tracking-widest text-font-tertiary/60">
              {t('output_label')}
            </p>
            <div className="rounded-2xl bg-font-secondary text-font-primary p-5 sm:p-6 flex flex-col gap-5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.4)]">
              {/* Output type list — interactive tabs */}
              <div className="flex flex-col" role="tablist">
                {outputs.map((label, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === activeIdx}
                    onClick={() => { setPaused(true); setActiveIdx(i); }}
                    className={`group flex items-center gap-3 font-mono text-nav border-b border-white/8 last:border-b-0 py-2.5 last:pb-0 first:pt-0 w-full text-left transition-colors duration-200 ${
                      i !== activeIdx ? 'cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <span className={`text-eyebrow w-6 shrink-0 transition-colors duration-200 ${
                      i === activeIdx
                        ? 'text-font-primary/65'
                        : 'text-font-primary/20 group-hover:text-font-primary/40'
                    }`}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className={`transition-colors duration-200 ${
                      i === activeIdx
                        ? 'text-font-primary'
                        : 'text-font-primary/45 group-hover:text-font-primary/70'
                    }`}>{label}</span>
                  </button>
                ))}
              </div>

              {/* Rotating example snippet */}
              <div className="pt-4 border-t border-white/8">
                <div className="rounded-xl bg-white/[0.05] p-3 sm:p-4 flex flex-col gap-3">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="font-mono text-eyebrow uppercase tracking-widest text-font-primary/65">
                      {exampleSnippets[activeIdx].label}
                    </span>
                    <span
                      key={`sub-${activeIdx}`}
                      className="text-nav text-font-primary/85 leading-snug animate-fade-in"
                    >
                      {exampleSnippets[activeIdx].sublabel}
                    </span>
                  </div>
                  <pre
                    key={activeIdx}
                    className="text-[11px] sm:text-[12px] leading-relaxed font-mono text-font-primary/55 overflow-hidden whitespace-pre animate-fade-in min-h-[140px]"
                  >
                    {exampleSnippets[activeIdx].snippet}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Closer line */}
        <p className="text-subheadline font-light text-font-secondary leading-relaxed max-w-6xl">
          {t('closer')}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers — rotation offsets, inline SVG icons, and example snippets
// ---------------------------------------------------------------------------

const ROTATIONS = [-3.2, 2.1, -1.4, 3.0, -2.6, 1.7];

function ConnectorArrow() {
  return (
    <>
      {/* Desktop: horizontal dashed arrow pointing right */}
      <svg
        width="28" height="14" viewBox="0 0 28 14"
        className="hidden lg:block text-font-secondary/25"
        aria-hidden
      >
        <line x1="0" y1="7" x2="20" y2="7" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
        <polyline points="16,3 24,7 16,11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      {/* Mobile: vertical dashed arrow pointing down */}
      <svg
        width="14" height="40" viewBox="0 0 14 40"
        className="lg:hidden text-font-secondary/25"
        aria-hidden
      >
        <line x1="7" y1="0" x2="7" y2="31" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
        <polyline points="3,27 7,35 11,27" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </>
  );
}

const INPUT_ICONS = [
  // Scanned document
  <svg key="scan" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="5" y="3" width="14" height="18" rx="1.5" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>,
  // PDF
  <svg key="pdf" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M14 3v4h4" />
    <text x="12" y="17" textAnchor="middle" fontSize="5.5" fill="currentColor" stroke="none">PDF</text>
  </svg>,
  // Voice waveform
  <svg key="voice" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M4 12v0M8 9v6M12 6v12M16 8v8M20 11v2" />
  </svg>,
  // Video
  <svg key="video" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="3" y="6" width="13" height="12" rx="1.5" />
    <path d="M16 10l5-3v10l-5-3z" />
  </svg>,
  // Free text
  <svg key="text" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M5 7h14M5 12h11M5 17h7" />
  </svg>,
  // Email envelope
  <svg key="email" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 7 9-7" />
  </svg>,
];

// Order matches `dataTranslation.examples` in the locale files. Each entry is
// a representative payload, kept short enough that rotation reads as a
// stylistic pulse rather than a wall of text.
const SNIPPETS: string[] = [
  // FHIR · Healthcare
`{
  "resourceType": "Encounter",
  "patient": "Patient/PT-04821",
  "period": { "start": "2026-05-08T09:14" },
  "diagnosis": [
    { "code": "I10", "label": "Hypertension" }
  ]
}`,
  // JSON · API payload
`{
  "case_id": "MA-2026-1184",
  "client": "Acme GmbH",
  "matter": "M&A advisory",
  "billing": { "rate_eur": 380, "hours": 14.5 },
  "status": "open"
}`,
  // SQL · Legacy DB
`INSERT INTO contracts
  (id, party_a, party_b, value_eur, signed_at)
VALUES
  ('C-9821', 'Acme GmbH', 'NetCo s.r.o.',
   1250000, '2026-05-08');`,
  // XML · Regulatory filing
`<Filing schema="EU-NIS2" version="2.1">
  <Entity vatId="DE314159265" />
  <Incident severity="major"
            reportedAt="2026-05-08T11:02Z" />
  <Affected systems="3" users="1840" />
</Filing>`,
];
